import { type MutableRefObject } from "react";

/**
 * Message types that we can send to the child iframe in order to control the behavior
 * of the app without running afoul of cross-frame security restrictions imposed by the
 * browser.
 */
export type SandboxAppCommand =
  | {
      // Requests that the sandbox app respond with the HTML source of its currently-loaded
      // document
      action: "source";
    }
  | {
      // Synchronizes the lesson UI's test-running state with marked controls in the sandbox app
      action: "runTestsState";
      disabled: boolean;
      loading: boolean;
    };

/**
 * Message types that iframe'd sandbox apps can send to the parent document where this
 * code is running: these postMessage calls are made via a script that's injected by the
 * proxy container.
 */
export type SandboxAppMessage =
  | {
      // Notifies the lesson UI that the sandbox-app iframe has loaded a new document
      // with the given location
      action: "location";
      protocol: string;
      host: string;
      pathname: string;
      search: string;
    }
  | {
      // Response from the sandbox-app iframe to a request for page source
      action: "source";
      data: string;
    }
  | {
      // Requests the lesson UI run the configured tests
      action: "runTests";
    };

/**
 * Parses the payload of a message event to a valid SandboxAppMessage object. If data
 * does not conform to a known SandboxAppMessage type, returns null instead. There's no
 * guarantee that sandbox apps won't make postMessage calls from scripts other than the
 * one injected by the proxy, so null results should be handled gracefully.
 */
export function parseSandboxAppMessage(data: unknown): SandboxAppMessage | null {
  // Require a valid object
  if (typeof data !== "object") {
    return null;
  }
  const obj = data as { [key: string]: unknown };

  // Parse 'location' messages if all expected fields are present
  if (obj["action"] === "location") {
    const protocol = obj["protocol"];
    if (typeof protocol !== "string") {
      return null;
    }
    const host = obj["host"];
    if (typeof host !== "string") {
      return null;
    }
    const pathname = obj["pathname"];
    if (typeof pathname !== "string") {
      return null;
    }
    const search = obj["search"];
    if (typeof search !== "string") {
      return null;
    }
    return {
      action: "location",
      protocol,
      host,
      pathname,
      search,
    };
  }

  // Parse 'source' messages if data (i.e. document source) is present
  if (obj["action"] === "source") {
    const data = obj["data"];
    if (typeof data !== "string") {
      return null;
    }
    return {
      action: "source",
      data,
    };
  }

  if (obj["action"] === "runTests") {
    return { action: "runTests" };
  }

  return null;
}

/**
 * Attempts to send a command to a sandbox app iframe by initiating a postMessage call.
 * Returns true if a postMessage call was made, false if the provided iframe or URL (for
 * determining origin) are not initialized.
 */
export function sendSandboxAppCommand(
  iframeRef: MutableRefObject<HTMLIFrameElement | null>,
  srcUrl: string,
  command: SandboxAppCommand
): boolean {
  // Require that the provided iframe element and URL are initialized
  if (!iframeRef.current || !iframeRef.current.contentWindow || !srcUrl) {
    return false;
  }

  // Parse the origin from the URL so we can appropriately restrict the postMessage
  // call, silently failing if the URL is malformed
  let origin = "";
  try {
    origin = new URL(srcUrl).origin;
  } catch (_error) {
    return false;
  }

  // Send the message to the iframe window
  iframeRef.current.contentWindow.postMessage(command, origin);
  return true;
}
